import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import AuthContext, { User } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/authApi';
import eventEmitter from '../utils/eventEmitter';

interface AuthState {
  user: User | null;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();

  const [data, setData] = useState<AuthState>({ user: null });
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const signOut = useCallback(async () => {
    try {
      // Envia POST /auth/logout/ para o backend de autenticação
      await authApi.post('/auth/logout/');
    } catch (err) {
      console.error('Erro ao fazer logout no servidor:', err);
    } finally {
      setData({ user: null });
      setAuthenticated(false);
      navigate('/login');
    }
  }, [navigate]);

  const checkAuth = useCallback(async () => {
    try {
      // Recupera info do usuário logado via /users/me (no backend de auth)
      const response = await authApi.get('/users/me');
      const user = response.data as User;

      setAuthenticated(true);
      setData({ user });
    } catch (error) {
      console.error('Erro na verificação de autenticação:', error);
      signOut();
    } finally {
      setLoading(false);
    }
  }, [signOut]);

  useEffect(() => {
    checkAuth();

    const handleLogout = () => {
      signOut();
    };
    eventEmitter.on('logout', handleLogout);

    return () => {
      eventEmitter.off('logout', handleLogout);
    };
  }, [checkAuth, signOut]);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        authenticated,
        loading,
        signOut,
        refreshUserToken: checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;